import { useRef, type ChangeEvent } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { AGRITOUR_SECTIONS } from './agritour-form-copy';
import type { AgriTourFiles } from './AgriTourScreen';
import styles from './AgriTourScreen.module.css';

interface AgriTourStepDProps {
  files: AgriTourFiles;
  fieldErrors: Record<string, string>;
  onFileChange: (key: keyof AgriTourFiles, file: File | null) => void;
  onVisualAppendixAdd: (files: File[]) => void;
  onVisualAppendixRemove: (index: number) => void;
}

export default function AgriTourStepD({
  files,
  fieldErrors,
  onFileChange,
  onVisualAppendixAdd,
  onVisualAppendixRemove,
}: AgriTourStepDProps) {
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  const handleSignatureEnd = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas || canvas.isEmpty()) return;
    canvas.getCanvas().toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'signature.png', { type: 'image/png' });
      onFileChange('signaturecrvya9ht', file);
    }, 'image/png');
  };

  const handleClearSignature = () => {
    sigCanvasRef.current?.clear();
    onFileChange('signaturecrvya9ht', null);
  };

  const handleAppendixPick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (!picked?.length) return;
    onVisualAppendixAdd(Array.from(picked));
    e.target.value = '';
  };

  return (
    <section aria-label={AGRITOUR_SECTIONS.d.title}>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>
          נספח חזותי לחלק ג׳
        </label>
        <p className={styles.fieldHint}>
          אנא צרף לשאלון זה (במידת האפשר והקיים): תמונות של המצב הקיים בשטח המיזם; הדמיות
          אדריכליות או סקיצות; או תמונות להמחשה של מיזמים דומים אם המיזם חדש לחלוטין.
          ניתן לבחור מספר קבצים.
        </p>
        {fieldErrors.file9djsb71e && (
          <p className={styles.fieldError} role="alert">{fieldErrors.file9djsb71e}</p>
        )}
        <div className={styles.fileRow}>
          <label className={styles.fileButton}>
            {files.file9djsb71e.length > 0 ? 'הוסף קבצים' : 'בחר קבצים'}
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              accept=".pdf,.jpg,.jpeg,.png,.zip"
              onChange={handleAppendixPick}
            />
          </label>
          {files.file9djsb71e.length === 0 ? (
            <span className={styles.fileName} style={{ color: 'var(--color-danger)' }}>
              נדרש
            </span>
          ) : null}
        </div>
        {files.file9djsb71e.length > 0 ? (
          <ul className={styles.fileList} aria-label="קבצים שנבחרו">
            {files.file9djsb71e.map((file, index) => (
              <li key={`${file.name}-${index}`} className={styles.fileListItem}>
                <span className={styles.fileName}>{file.name}</span>
                <button
                  type="button"
                  className={styles.fileRemove}
                  onClick={() => onVisualAppendixRemove(index)}
                >
                  הסר
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className={styles.field}>
        <label className={`${styles.fieldLabel} ${styles.required}`}>חתימת היזם</label>
        <p className={styles.fieldHint}>חתום בתוך הריבוע באמצעות העכבר או המגע</p>
        {fieldErrors.signaturecrvya9ht && (
          <p className={styles.fieldError} role="alert">{fieldErrors.signaturecrvya9ht}</p>
        )}
        <div
          className={`${styles.signatureWrap} ${fieldErrors.signaturecrvya9ht ? styles.signatureWrapError : ''}`}
        >
          <SignatureCanvas
            ref={sigCanvasRef}
            canvasProps={{
              className: styles.signatureCanvas,
            }}
            onEnd={handleSignatureEnd}
            backgroundColor="#ffffff"
            penColor="#1a2e2a"
          />
        </div>
        <button type="button" className={styles.clearSignature} onClick={handleClearSignature}>
          נקה
        </button>
        {files.signaturecrvya9ht ? (
          <p className={styles.fieldHint} style={{ color: 'var(--color-accent)' }}>
            ✓ חתימה נשמרה
          </p>
        ) : null}
      </div>
    </section>
  );
}
